import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';
const ProductionOutputDashboard: React.FC = () => {
  // KPI Data
  const kpis = [{
    title: 'Daily Output',
    value: '33,100 Units',
    change: '+2.8%',
    isPositive: true,
    subtitle: 'Today'
  }, {
    title: 'Plan vs Actual',
    value: '100.3%',
    change: '+0.3%',
    isPositive: true,
    subtitle: 'Above target'
  }, {
    title: 'Weekly Average',
    value: '32,817 Units',
    change: '+1.5%',
    isPositive: true,
    subtitle: 'This week'
  }, {
    title: 'Line Efficiency',
    value: '96.7%',
    change: '+2.1%',
    isPositive: true,
    subtitle: 'Avg across lines'
  }];
  // Shift-wise Output Comparison Data
  const shiftOutputData = [{
    shift: 'Shift A\n(6:00-14:00)',
    output: 11200,
    target: 11000,
    efficiency: 101.8
  }, {
    shift: 'Shift B\n(14:00-22:00)',
    output: 11600,
    target: 11000,
    efficiency: 105.5
  }, {
    shift: 'Shift C\n(22:00-6:00)',
    output: 10300,
    target: 11000,
    efficiency: 93.6
  }];
  // Line-wise Output Trend (Last 7 Days)
  const lineOutputTrendData = [{
    day: 'Mon',
    Line1: 6450,
    Line2: 6800,
    Line3: 6200,
    Line4: 6550,
    Line5: 6500
  }, {
    day: 'Tue',
    Line1: 6520,
    Line2: 6850,
    Line3: 6250,
    Line4: 6600,
    Line5: 6580
  }, {
    day: 'Wed',
    Line1: 6480,
    Line2: 6820,
    Line3: 6180,
    Line4: 6580,
    Line5: 6540
  }, {
    day: 'Thu',
    Line1: 6600,
    Line2: 6900,
    Line3: 6300,
    Line4: 6650,
    Line5: 6650
  }, {
    day: 'Fri',
    Line1: 6550,
    Line2: 6880,
    Line3: 6280,
    Line4: 6620,
    Line5: 6620
  }, {
    day: 'Sat',
    Line1: 6350,
    Line2: 6650,
    Line3: 6100,
    Line4: 6450,
    Line5: 6450
  }, {
    day: 'Today',
    Line1: 6580,
    Line2: 6920,
    Line3: 6320,
    Line4: 6680,
    Line5: 6600
  }];
  const hourlyData = [{
    hour: '00:00',
    output: 850,
    target: 900
  }, {
    hour: '04:00',
    output: 870,
    target: 900
  }, {
    hour: '08:00',
    output: 920,
    target: 900
  }, {
    hour: '12:00',
    output: 910,
    target: 900
  }, {
    hour: '16:00',
    output: 895,
    target: 900
  }, {
    hour: '20:00',
    output: 880,
    target: 900
  }];
  const linePerformanceData = [{
    line: 'Line 1',
    output: 145000,
    target: 150000,
    efficiency: 96.7
  }, {
    line: 'Line 2',
    output: 152000,
    target: 150000,
    efficiency: 101.3
  }, {
    line: 'Line 3',
    output: 138000,
    target: 150000,
    efficiency: 92.0
  }, {
    line: 'Line 4',
    output: 148000,
    target: 150000,
    efficiency: 98.7
  }, {
    line: 'Line 5',
    output: 142000,
    target: 150000,
    efficiency: 94.7
  }];
  const bottleneckData = [{
    process: 'Molding',
    utilization: 95,
    bottleneck: true
  }, {
    process: 'Assembly',
    utilization: 78,
    bottleneck: false
  }, {
    process: 'Testing',
    utilization: 92,
    bottleneck: true
  }, {
    process: 'Packaging',
    utilization: 68,
    bottleneck: false
  }, {
    process: 'Quality Check',
    utilization: 88,
    bottleneck: false
  }];
  const dailyOutputData = [{
    day: 'Mon',
    actual: 32500,
    target: 33000
  }, {
    day: 'Tue',
    actual: 33200,
    target: 33000
  }, {
    day: 'Wed',
    actual: 32800,
    target: 33000
  }, {
    day: 'Thu',
    actual: 33500,
    target: 33000
  }, {
    day: 'Fri',
    actual: 33100,
    target: 33000
  }, {
    day: 'Sat',
    actual: 31800,
    target: 32000
  }];
  // Production Summary Table Data
  const productionSummaryData = [{
    line: 'Line 1',
    shift: 'Shift A',
    output: 3750,
    target: 3667,
    efficiency: 102.3,
    downtime: 15,
    scrapRate: 1.8,
    oee: 78.5
  }, {
    line: 'Line 1',
    shift: 'Shift B',
    output: 3900,
    target: 3667,
    efficiency: 106.4,
    downtime: 8,
    scrapRate: 1.5,
    oee: 82.3
  }, {
    line: 'Line 1',
    shift: 'Shift C',
    output: 3450,
    target: 3666,
    efficiency: 94.1,
    downtime: 22,
    scrapRate: 2.1,
    oee: 72.8
  }, {
    line: 'Line 2',
    shift: 'Shift A',
    output: 3850,
    target: 3667,
    efficiency: 105.0,
    downtime: 12,
    scrapRate: 1.6,
    oee: 80.2
  }, {
    line: 'Line 2',
    shift: 'Shift B',
    output: 4050,
    target: 3667,
    efficiency: 110.4,
    downtime: 5,
    scrapRate: 1.3,
    oee: 85.8
  }, {
    line: 'Line 2',
    shift: 'Shift C',
    output: 3600,
    target: 3666,
    efficiency: 98.2,
    downtime: 18,
    scrapRate: 1.9,
    oee: 76.5
  }, {
    line: 'Line 3',
    shift: 'Shift A',
    output: 3450,
    target: 3667,
    efficiency: 94.1,
    downtime: 20,
    scrapRate: 2.3,
    oee: 73.2
  }, {
    line: 'Line 3',
    shift: 'Shift B',
    output: 3550,
    target: 3667,
    efficiency: 96.8,
    downtime: 16,
    scrapRate: 2.0,
    oee: 75.8
  }, {
    line: 'Line 3',
    shift: 'Shift C',
    output: 3200,
    target: 3666,
    efficiency: 87.3,
    downtime: 28,
    scrapRate: 2.6,
    oee: 68.5
  }, {
    line: 'Line 4',
    shift: 'Shift A',
    output: 3700,
    target: 3667,
    efficiency: 100.9,
    downtime: 14,
    scrapRate: 1.7,
    oee: 77.8
  }, {
    line: 'Line 4',
    shift: 'Shift B',
    output: 3850,
    target: 3667,
    efficiency: 105.0,
    downtime: 10,
    scrapRate: 1.5,
    oee: 81.5
  }, {
    line: 'Line 4',
    shift: 'Shift C',
    output: 3400,
    target: 3666,
    efficiency: 92.7,
    downtime: 24,
    scrapRate: 2.2,
    oee: 71.2
  }, {
    line: 'Line 5',
    shift: 'Shift A',
    output: 3600,
    target: 3667,
    efficiency: 98.2,
    downtime: 16,
    scrapRate: 1.9,
    oee: 76.2
  }, {
    line: 'Line 5',
    shift: 'Shift B',
    output: 3750,
    target: 3667,
    efficiency: 102.3,
    downtime: 12,
    scrapRate: 1.7,
    oee: 79.5
  }, {
    line: 'Line 5',
    shift: 'Shift C',
    output: 3350,
    target: 3666,
    efficiency: 91.4,
    downtime: 25,
    scrapRate: 2.3,
    oee: 70.8
  }];
  return <div data-id="element-6206">
      <DescriptionPanel title="Production Output Analysis" description="Real-time and historical production output tracking across all lines and shifts. Monitor hourly performance, identify bottlenecks, and optimize production flow." insights={['Shift B consistently outperforming with 105.5% efficiency - best practices to replicate', 'Line 2 exceeding target by 1.3% - best performer this month', 'Shift C underperforming at 93.6% - night shift optimization needed', 'Molding and Testing identified as bottleneck processes at 95% and 92% utilization']} data-id="element-6207" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-6208">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-6209">
          Production Output Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-6210">
          {kpis.map((kpi, index) => <Card key={index} className="p-5 bg-gradient-to-br from-white to-gray-50" data-id="element-6211">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide" data-id="element-6212">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-2" data-id="element-6213">{kpi.subtitle}</div>
              <div className="text-3xl font-bold text-gray-900 mb-2" data-id="element-6214">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-6215">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-6216" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-6217" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* Shift-wise Output Comparison */}
      <Card className="p-6 mb-8" data-id="element-6218">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6219">
          Shift-wise Output Comparison
        </h3>
        <ResponsiveContainer width="100%" height={350} data-id="element-6220">
          <ComposedChart data={shiftOutputData} data-id="element-6221">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-6222" />
            <XAxis dataKey="shift" tick={{
            fontSize: 11
          }} data-id="element-6223" />
            <YAxis yAxisId="left" tick={{
            fontSize: 11
          }} data-id="element-6224" />
            <YAxis yAxisId="right" orientation="right" tick={{
            fontSize: 11
          }} domain={[85, 110]} data-id="element-6225" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-6226" />
            <Legend data-id="element-6227" />
            <Bar yAxisId="left" dataKey="output" fill="#3b82f6" name="Actual Output" data-id="element-6228" />
            <Bar yAxisId="left" dataKey="target" fill="#e5e7eb" name="Target" data-id="element-6229" />
            <Line yAxisId="right" type="monotone" dataKey="efficiency" stroke="#10b981" strokeWidth={3} name="Efficiency %" dot={{
            r: 5
          }} data-id="element-6230" />
          </ComposedChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6231">
          <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-6232">
            <div className="text-center" data-id="element-6233">
              <div className="text-gray-600" data-id="element-6234">Best Shift</div>
              <div className="text-xl font-bold text-green-600" data-id="element-6235">Shift B</div>
              <div className="text-xs text-gray-500" data-id="element-6236">105.5% efficiency</div>
            </div>
            <div className="text-center" data-id="element-6237">
              <div className="text-gray-600" data-id="element-6238">Total Daily Output</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6239">33,100</div>
              <div className="text-xs text-gray-500" data-id="element-6240">units</div>
            </div>
            <div className="text-center" data-id="element-6241">
              <div className="text-gray-600" data-id="element-6242">Improvement Needed</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-6243">Shift C</div>
              <div className="text-xs text-gray-500" data-id="element-6244">93.6% efficiency</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Line-wise Output Trend */}
      <Card className="p-6 mb-8" data-id="element-6245">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6246">
          Line-wise Output Trend (Last 7 Days)
        </h3>
        <ResponsiveContainer width="100%" height={350} data-id="element-6247">
          <LineChart data={lineOutputTrendData} data-id="element-6248">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-6249" />
            <XAxis dataKey="day" tick={{
            fontSize: 12
          }} data-id="element-6250" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-6251" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-6252" />
            <Legend data-id="element-6253" />
            <Line type="monotone" dataKey="Line1" stroke="#3b82f6" strokeWidth={2} name="Line 1" dot={{
            r: 3
          }} data-id="element-6254" />
            <Line type="monotone" dataKey="Line2" stroke="#10b981" strokeWidth={2} name="Line 2" dot={{
            r: 3
          }} data-id="element-6255" />
            <Line type="monotone" dataKey="Line3" stroke="#f59e0b" strokeWidth={2} name="Line 3" dot={{
            r: 3
          }} data-id="element-6256" />
            <Line type="monotone" dataKey="Line4" stroke="#ef4444" strokeWidth={2} name="Line 4" dot={{
            r: 3
          }} data-id="element-6257" />
            <Line type="monotone" dataKey="Line5" stroke="#8b5cf6" strokeWidth={2} name="Line 5" dot={{
            r: 3
          }} data-id="element-6258" />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-6259">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-6260">
            <div className="text-center" data-id="element-6261">
              <div className="text-gray-600" data-id="element-6262">Line 1</div>
              <div className="text-lg font-bold text-blue-600" data-id="element-6263">6,580</div>
              <div className="text-xs text-gray-500" data-id="element-6264">today</div>
            </div>
            <div className="text-center" data-id="element-6265">
              <div className="text-gray-600" data-id="element-6266">Line 2</div>
              <div className="text-lg font-bold text-green-600" data-id="element-6267">6,920</div>
              <div className="text-xs text-gray-500" data-id="element-6268">today</div>
            </div>
            <div className="text-center" data-id="element-6269">
              <div className="text-gray-600" data-id="element-6270">Line 3</div>
              <div className="text-lg font-bold text-orange-600" data-id="element-6271">6,320</div>
              <div className="text-xs text-gray-500" data-id="element-6272">today</div>
            </div>
            <div className="text-center" data-id="element-6273">
              <div className="text-gray-600" data-id="element-6274">Line 4</div>
              <div className="text-lg font-bold text-red-600" data-id="element-6275">6,680</div>
              <div className="text-xs text-gray-500" data-id="element-6276">today</div>
            </div>
            <div className="text-center" data-id="element-6277">
              <div className="text-gray-600" data-id="element-6278">Line 5</div>
              <div className="text-lg font-bold text-purple-600" data-id="element-6279">6,600</div>
              <div className="text-xs text-gray-500" data-id="element-6280">today</div>
            </div>
          </div>
        </div>
      </Card>
      {/* Bottleneck Analysis */}
      <Card className="p-6 mb-8" data-id="element-6281">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6282">
          Process Bottleneck Visualization
        </h3>
        <div className="space-y-4" data-id="element-6283">
          {bottleneckData.map((item, index) => <div key={index} data-id="element-6284">
              <div className="flex items-center justify-between mb-2" data-id="element-6285">
                <div className="flex items-center" data-id="element-6286">
                  <span className="text-sm font-medium text-gray-700" data-id="element-6287">
                    {item.process}
                  </span>
                  {item.bottleneck && <span className="ml-2 flex items-center text-xs text-red-600" data-id="element-6288">
                      <AlertTriangleIcon size={14} className="mr-1" data-id="element-6289" />
                      Bottleneck
                    </span>}
                </div>
                <span className="text-sm font-semibold text-gray-800" data-id="element-6290">
                  {item.utilization}%
                </span>
              </div>
              <div className="relative h-8 bg-gray-100 rounded-lg overflow-hidden" data-id="element-6291">
                <div className={`h-full ${item.bottleneck ? 'bg-red-500' : 'bg-green-500'}`} style={{
              width: `${item.utilization}%`
            }} data-id="element-6292" />
              </div>
            </div>)}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600" data-id="element-6293">
          <p data-id="element-6294">
            <strong data-id="element-6295">Note:</strong> Processes above 90% utilization are
            considered bottlenecks and require capacity expansion or process
            optimization.
          </p>
        </div>
      </Card>
      {/* Production Summary Table */}
      <Card className="p-6" data-id="element-6296">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-6297">
          Production Summary - Line & Shift Level
        </h3>
        <div className="overflow-x-auto" data-id="element-6298">
          <table className="w-full" data-id="element-6299">
            <thead data-id="element-6300">
              <tr className="border-b-2 border-gray-300" data-id="element-6301">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6302">
                  Line
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6303">
                  Shift
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6304">
                  Output
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6305">
                  Target
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6306">
                  Efficiency %
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6307">
                  Downtime (min)
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6308">
                  Scrap Rate %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-6309">
                  OEE %
                </th>
              </tr>
            </thead>
            <tbody data-id="element-6310">
              {productionSummaryData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-6311">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-6312">
                    {row.line}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-6313">
                    {row.shift}
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-6314">
                    {row.output.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-6315">
                    {row.target.toLocaleString()}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6316">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.efficiency >= 100 ? 'bg-green-100 text-green-700' : row.efficiency >= 95 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-6317">
                      {row.efficiency.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-6318">
                    {row.downtime}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6319">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.scrapRate <= 1.7 ? 'bg-green-100 text-green-700' : row.scrapRate <= 2.0 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-6320">
                      {row.scrapRate.toFixed(1)}%
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-6321">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${row.oee >= 80 ? 'bg-green-100 text-green-700' : row.oee >= 75 ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-6322">
                      {row.oee.toFixed(1)}%
                    </span>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-6323">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-6324">
            <div className="text-center" data-id="element-6325">
              <div className="text-gray-600" data-id="element-6326">Total Output</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-6327">55,050</div>
              <div className="text-xs text-gray-500" data-id="element-6328">units today</div>
            </div>
            <div className="text-center" data-id="element-6329">
              <div className="text-gray-600" data-id="element-6330">Best Performer</div>
              <div className="text-xl font-bold text-green-600" data-id="element-6331">
                Line 2, Shift B
              </div>
              <div className="text-xs text-gray-500" data-id="element-6332">110.4% efficiency</div>
            </div>
            <div className="text-center" data-id="element-6333">
              <div className="text-gray-600" data-id="element-6334">Avg Efficiency</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-6335">98.7%</div>
              <div className="text-xs text-gray-500" data-id="element-6336">across all lines</div>
            </div>
            <div className="text-center" data-id="element-6337">
              <div className="text-gray-600" data-id="element-6338">Total Downtime</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-6339">255 min</div>
              <div className="text-xs text-gray-500" data-id="element-6340">today</div>
            </div>
            <div className="text-center" data-id="element-6341">
              <div className="text-gray-600" data-id="element-6342">Avg OEE</div>
              <div className="text-xl font-bold text-purple-600" data-id="element-6343">76.5%</div>
              <div className="text-xs text-gray-500" data-id="element-6344">across all shifts</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default ProductionOutputDashboard;